import _ from 'lodash';
import safeDecodeURIComponent from 'fast-decode-uri-component';
import safeEncodeURIComponent from 'encodeurl';
import { RISON } from 'rison2';

/**
 * Get notified of changes on a single parameter in a hash string
 * e.g. when window.location.hash changes from '#a?one=1&two=2' to '#a?one=first&two=2'
 */
export class UrlModel {
	/**
	 * Set up variables
	 */
	constructor() {
		/**
		 * Key-value pairs representing the current params in the hash string
		 * @type {Object}
		 * @private
		 */
		this.values = {};
	}

	/**
	 * Parse the given query and store as current values
	 * e.g. 'one=1&two=2' produces {one: 1, two: 2}
	 * @returns {UrlModel}
	 * @chainable
	 * @private
	 */
	load(query) {
		this.values = UrlModel.parse(query);
		return this;
	}

	/**
	 * Return the query as a string
	 * @returns {String}
	 */
	stringify() {
		return UrlModel.stringify(this.values);
	}

	/**
	 * Get the current value of the given param
	 * @param {String} param
	 * @returns {String}
	 */
	get(param) {
		return this.values[param];
	}

	/**
	 * Set the value of the given param and update window.location.hash
	 * @param {String} param
	 * @param {*} value
	 * @returns {UrlModel}
	 * @chainable
	 */
	set(param, value) {
		this.values[param] = value;
		return this;
	}

	/**
	 * Get the difference between this data and the given UrlModel object
	 * @param {UrlModel} rightModel  The model to compare to
	 * @return {Array}  An array of objects with keys {param, left, right}
	 */
	diff(rightModel) {
		const diff = [];
		_.forOwn(this.values, (left, param) => {
			const right = rightModel.values[param];
			if (!_.isEqual(left, right)) {
				diff.push({ param, left, right });
			}
		});
		_.forOwn(rightModel.values, (right, param) => {
			if (!(param in this.values)) {
				const left = undefined;
				diff.push({ param, left, right });
			}
		});
		return diff;
	}

	/**
	 * Parse a query string into an object
	 * e.g. 'one=1&two=!t' produces {one: 1, two: true}
	 * @returns {Object}
	 */
	static parse(query) {
		if (!query) {
			return {};
		}
		const pairs = query.split('&');
		const values = {};
		pairs.forEach(pair => {
			let [param, value] = pair.split('=');
			if (value === undefined) {
				return;
			}
			param = UrlModel._uriDecode(param);
			value = UrlModel._uriDecode(value);
			const parsedValue = UrlModel.risonParse(value);
			if (parsedValue === undefined) {
				// invalid rison; interpret as a string
				values[param] = value;
			} else {
				values[param] = parsedValue;
			}
		});
		return values;
	}

	/**
	 * Build a query string from an object
	 * e.g. {one: 1, two: [2,2]} produces 'one=1&two=!(2,2)'
	 * @param {Object} params
	 * @returns {String}
	 * @private
	 */
	static stringify(params) {
		const pairs = [];
		_.forOwn(params, (value, param) => {
			if (value === undefined) {
				return;
			}
			const encParam = UrlModel._uriEncode(param);
			let encValue = UrlModel.risonStringify(value);
			if (encValue) {
				encValue = UrlModel._uriEncode(encValue);
			}
			if (typeof encValue == 'string') {
				pairs.push(`${encParam}=${encValue}`);
			}
		});
		// improve cacheability by using predictable order
		pairs.sort();
		return pairs.join('&');
	}

	/**
	 * Encode using rison. If errors are caught, undefined is returned.
	 * @param {*} value
	 * @returns {String}
	 */
	static risonStringify(value) {
		try {
			return RISON.stringify(value);
		} catch (e) {}
		return undefined;
	}

	/**
	 * Decode using rison. If errors are caught, undefined is returned.
	 * @param {String} dataString
	 * @returns {*}
	 */
	static risonParse(dataString) {
		if (typeof dataString != 'string') {
			return undefined;
		}
		try {
			return RISON.parse(dataString);
		} catch (e) {
			return undefined;
		}
	}

	/**
	 * Encode characters for URI, but allow some common ones that are actually ok
	 * @param {String} str  The rison-encoded string
	 * @return {String}  The string suitable for the URL bar
	 * @private
	 */
	static _uriEncode(str) {
		return safeEncodeURIComponent(str || '')
			.replace(/#/g, '%23')
			.replace(/&/g, '%26')
			.replace(/\//g, '%2F')
			.replace(/\+/g, '%2B')
			.replace(/=/g, '%3D')
			.replace(/\?/g, '%3F');
	}

	/**
	 * Decode characters, falling back to npm safe-decode-uri-component on exception
	 * @param {String} str  The URL bar string
	 * @return {String}  The string suitable for rison decode
	 * @private
	 */
	static _uriDecode(str) {
		return safeDecodeURIComponent(str || '');
	}
}
